/**
 * Keeps a long-lived tab on the newest build. index.html is served no-cache and names
 * the hashed entry bundle, so a tab left open (or restored by the phone after the app
 * was closed) can compare the bundle it runs against the one the server now advertises.
 * A newer build always raises a notice (see UpdateBanner) with a Refresh-now button. If nobody taps it, the
 * page reloads by itself once the user has been idle for IDLE_RELOAD_MS and nothing would be lost; the reload
 * keeps the tab, layout and scroll position (see reloadState).
 */
import { flushSaves, hasPendingSaves } from './saveQueue';
import { reloadPage } from './reloadState';

const ENTRY_RE = /\/assets\/index-[\w-]+\.js/;
// Safety net only: the socket reconnect and tab focus are the real triggers.
const RECHECK_MS = 10 * 60 * 1000;
/** How long without any interaction before the waiting update is applied by itself. */
export const IDLE_RELOAD_MS = 2 * 60 * 1000;

/** The entry bundle this page is running, or null in dev (unhashed, nothing to compare). */
function runningEntry(): string | null {
  for (const s of Array.from(document.scripts)) {
    const m = ENTRY_RE.exec(s.src);
    if (m) return m[0];
  }
  return null;
}

async function advertisedEntry(): Promise<string | null> {
  const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) return null;
  const m = ENTRY_RE.exec(await res.text());
  return m ? m[0] : null;
}

/** True while a reload would throw something away that the save queue cannot rescue: the
 *  wizard or an officer card being edited (held in the form until Save), or a text field
 *  being typed into. Queued grid edits are NOT a blocker — they are flushed first. */
export function hasUnsavedWork(): boolean {
  const cl = document.body.classList;
  if (cl.contains('wizard-open') || cl.contains('edit-panel-open') || cl.contains('edit-panel-open-bottom')) return true;
  const a = document.activeElement;
  return !!a && (a.tagName === 'TEXTAREA' || (a.tagName === 'INPUT' && (a as HTMLInputElement).type !== 'checkbox'));
}

/** Let in-flight edits land (bounded, so a dead network cannot hold the reload hostage), then reload. */
export async function reloadWhenSaved(): Promise<void> {
  if (hasPendingSaves()) {
    await Promise.race([flushSaves().catch(() => undefined), new Promise((r) => setTimeout(r, 8000))]);
    if (hasPendingSaves() || hasUnsavedWork()) { setStale(true); return; }
  }
  reloadPage();
}

/** The Refresh-now button: the user chose it, so reload even with a card open — after queued edits are saved. */
export async function refreshNow(): Promise<void> {
  if (hasPendingSaves()) {
    await Promise.race([flushSaves().catch(() => undefined), new Promise((r) => setTimeout(r, 8000))]);
  }
  reloadPage();
}

// Last time the user touched the page. Starts over whenever the tab comes back to the front, so someone
// returning to a long-idle tab gets their full two minutes instead of a reload under their hands.
let lastActive = Date.now();
const markActive = () => { lastActive = Date.now(); };
['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach((e) =>
  window.addEventListener(e, markActive, { capture: true, passive: true }));
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') markActive(); });

type Listener = (stale: boolean) => void;
const listeners = new Set<Listener>();
let stale = false;

export function onUpdateAvailable(fn: Listener): () => void {
  listeners.add(fn);
  fn(stale);
  return () => { listeners.delete(fn); };
}

let staleWatch: number | undefined;
function setStale(v: boolean) {
  stale = v;
  // Once a newer build is waiting, apply it after the user has been idle for IDLE_RELOAD_MS (and, if work is
  // open, only once that is saved).
  if (v && staleWatch === undefined) {
    markActive();
    staleWatch = window.setInterval(() => {
      if (Date.now() - lastActive >= IDLE_RELOAD_MS && !hasUnsavedWork()) void reloadWhenSaved();
    }, 5000);
  } else if (!v && staleWatch !== undefined) { clearInterval(staleWatch); staleWatch = undefined; }
  listeners.forEach((l) => l(v));
}

// Reconnect storms on a flaky network must not turn into a fetch storm.
const MIN_GAP_MS = 20_000;
let checking = false;
let lastCheck = 0;
async function check(): Promise<void> {
  const mine = runningEntry();
  if (!mine || checking || Date.now() - lastCheck < MIN_GAP_MS) return;
  checking = true;
  lastCheck = Date.now();
  try {
    const latest = await advertisedEntry();
    if (!latest || latest === mine) { setStale(false); return; }
    setStale(true);
  } catch { /* offline / server restarting — try again next time */ }
  finally { checking = false; }
}

/** Check now; safe to call often (one check at a time, no-op in dev). */
export function checkForUpdate(): void {
  void check();
}

export function startUpdateChecks(): void {
  if (!runningEntry()) return;
  void check();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.addEventListener('online', () => void check());
  window.setInterval(() => { if (document.visibilityState === 'visible') void check(); }, RECHECK_MS);
}
